'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { beginRouteTransition } from '@/lib/navigation-pending'
import {
  cityPath,
  cityToSlug,
  foldCityKey,
  POPULAR_WEATHER_CITIES,
} from '@/lib/weather-city'

export type HubCityWeather = {
  city: string
  temperature: number | null
  feelsLike: number | null
  humidity: number | null
  condition: string | null
  icon: string | null
}

type CityOption = { city: string }

function formatTemp(n: number | null | undefined) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—'
  return String(Math.round(n))
}

export default function WeatherHubClient({
  popular,
  cities,
}: {
  popular: HubCityWeather[]
  cities: CityOption[]
}) {
  const router = useRouter()
  const pickerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    if (!pickerOpen) return
    const onPointer = (e: MouseEvent) => {
      if (!pickerRef.current?.contains(e.target as Node)) setPickerOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  const filtered = useMemo(() => {
    const q = foldCityKey(query)
    if (!q) return cities.slice(0, 60)
    return cities
      .filter((c) => {
        const key = foldCityKey(c.city)
        return (
          key.includes(q) ||
          cityToSlug(c.city).includes(q.replace(/\s+/g, '-'))
        )
      })
      .slice(0, 60)
  }, [cities, query])

  const goCity = (city: string) => {
    setPickerOpen(false)
    setQuery('')
    const href = cityPath(city)
    beginRouteTransition(href)
    router.push(href)
  }

  const cards = useMemo(() => {
    const byCity = new Map(popular.map((p) => [foldCityKey(p.city), p]))
    return POPULAR_WEATHER_CITIES.map((name) => {
      const hit = byCity.get(foldCityKey(name))
      return (
        hit ?? {
          city: name,
          temperature: null,
          feelsLike: null,
          humidity: null,
          condition: null,
          icon: null,
        }
      )
    })
  }, [popular])

  const allCities = useMemo(() => {
    const names = cities.map((c) => c.city).filter(Boolean)
    return [...names].sort((a, b) =>
      a.localeCompare(b, 'tr', { sensitivity: 'base' }),
    )
  }, [cities])

  return (
    <div className="space-y-6">
      <nav className="text-xs text-gray-500 dark:text-gray-400" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li>
            <Link href="/" className="hover:text-red-600">
              Haber.com
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="font-medium text-gray-800 dark:text-gray-200">
            Hava Durumu
          </li>
        </ol>
      </nav>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            Hava Durumu
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            İl seçin veya arayın — saatlik ve 15 günlük tahmin
          </p>
        </div>

        <div ref={pickerRef} className="relative w-full sm:max-w-xs">
          <label className="sr-only" htmlFor="weather-hub-city">
            Şehir ara
          </label>
          <input
            id="weather-hub-city"
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPickerOpen(true)
            }}
            onFocus={() => setPickerOpen(true)}
            placeholder="Şehir ara… (ör. Ankara)"
            autoComplete="off"
            className="h-11 w-full rounded-[5px] border border-gray-200 bg-white px-3 text-sm text-gray-900 outline-none ring-sky-500/30 placeholder:text-gray-400 focus:border-sky-400 focus:ring-2 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
          {pickerOpen ? (
            <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-[5px] border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-950">
              {filtered.length ? (
                filtered.map((c) => (
                  <li key={c.city}>
                    <button
                      type="button"
                      onClick={() => goCity(c.city)}
                      className="flex w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-sky-50 dark:text-gray-100 dark:hover:bg-slate-900"
                    >
                      {c.city}
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-sm text-gray-500">Sonuç yok</li>
              )}
            </ul>
          ) : null}
        </div>
      </div>

      <section>
        <h2 className="mb-3 text-sm font-bold text-gray-900 dark:text-white">
          Büyükşehirler
        </h2>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {cards.map((item) => (
            <Link
              key={item.city}
              href={cityPath(item.city)}
              prefetch={false}
              className="group overflow-hidden rounded-[5px] border border-gray-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 p-3.5 transition-shadow hover:border-sky-300 hover:shadow-md dark:border-slate-700 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 dark:hover:border-sky-600"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-gray-900 group-hover:text-sky-800 dark:text-white dark:group-hover:text-sky-200">
                    {item.city}
                  </p>
                  <p className="mt-0.5 line-clamp-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {item.condition || 'Tahmin'}
                  </p>
                </div>
                {item.icon ? (
                  <Image
                    src={`/weather-icons/${item.icon}`}
                    alt=""
                    width={40}
                    height={40}
                    className="h-10 w-10 shrink-0"
                  />
                ) : (
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-bold text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                    °
                  </span>
                )}
              </div>
              <p className="mt-3 text-3xl font-black tabular-nums tracking-tight text-gray-900 dark:text-white">
                {formatTemp(item.temperature)}
                <span className="text-lg font-bold text-gray-500">°</span>
              </p>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Hissedilen {formatTemp(item.feelsLike ?? item.temperature)}°
                {typeof item.humidity === 'number' ? (
                  <> · Nem %{Math.round(item.humidity)}</>
                ) : null}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-sm font-bold text-gray-900 dark:text-white">
          Tüm iller
        </h2>
        <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8">
          {allCities.map((city) => (
            <Link
              key={city}
              href={cityPath(city)}
              prefetch={false}
              className="truncate rounded-[5px] border border-gray-200 bg-white px-2 py-1.5 text-center text-[12px] font-medium text-gray-700 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800 dark:border-slate-700 dark:bg-slate-950 dark:text-gray-200 dark:hover:border-sky-600 dark:hover:bg-slate-900 dark:hover:text-sky-200"
            >
              {city}
            </Link>
          ))}
        </div>
      </section>
    </div>
  )
}
